// Copyright (c) Docugami, Inc. All rights reserved.**/

import { DirectionalHint, Spinner, TooltipDelay, TooltipHost } from "@fluentui/react";
import { homeGray, primaryTheme } from "components/shell/presentation/AppStyles";
import { dgIconNames, useDgIcons } from "hooks/dgIcons";
import { useEffect } from "react";
import styled, { css } from "styled-components"

const CheckboxConfirmableCss = css<CheckboxProps>`
    cursor: pointer;

    i {
        display: none;
    }

    :hover {
        i {
            display: block;
        }
        &::before {
            background: ${props => props.confirmedBackgroundColor};
            opacity: .5;
        }
    }
`;

const CheckboxConfirmedCss = css`
    i {
        display: block;
    }
`;

interface CheckboxProps {
    confirmedBackgroundColor?: string;
    isConfirmed?: boolean;
    pulse?: boolean;
    readOnly?: boolean;
}

const PulseAnimationCss = css`
    @keyframes pulseAnimation {
        0%, 100% { opacity: 0; }
        33%, 66% { opacity: 1; }
    }
    animation: pulseAnimation 3s ease-in-out infinite;
`

const Root = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 16px;
    height: 16px;
    box-sizing: border-box;
`;

const CheckboxVisual = styled.div<CheckboxProps>`
    position: relative; // Required for pseudo-element positioning
    width: 100%;
    height: 100%;
    z-index: 0;
    cursor: default;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    background: ${primaryTheme.palette.white};

    border: 1px solid ${homeGray};
    border-radius: 4px;

    // Icon font styling
    i {
        display: none;
        color: ${homeGray};
        font-size: 10px;
        line-height: 15px;
        text-align: center;
    }

    // Background styling - using a pseudo-element to allow us to animate the background opacity without affecting the content
    &::before {
        content: '';
        pointer-events: none;
        position: absolute;
        top: -1px;
        left: -1px;
        right: -1px;
        bottom: -1px;
        border-radius: inherit;
        border: inherit;
        z-index: -1;
        background: ${props => !!props.isConfirmed || props.pulse ? props.confirmedBackgroundColor : "inherit"};
        ${props => !!props.pulse && PulseAnimationCss}
    }

    ${props => !props.isConfirmed && !props.readOnly && CheckboxConfirmableCss};
    ${props => !!props.isConfirmed && CheckboxConfirmedCss};
`;

export interface ConfirmationButtonProps {
    isConfirmed?: boolean;
    isConfirming?: boolean;
    onClick?: React.MouseEventHandler<HTMLDivElement>;
    confirmedBackgroundColor?: string;
    tooltip?: string | JSX.Element | JSX.Element[];
    hidden?: boolean;
    pulse?: boolean;
    'data-automation-id'?: string;
    readOnly?: boolean;
}

export const ConfirmationButton = (props: ConfirmationButtonProps): JSX.Element | null => {
    const dgIcons = useDgIcons();

    useEffect(() => {
        if (!!props.pulse) {
            const animations = document.getAnimations();
            let time: CSSNumberish | null = null;
            for (const animation of animations) {
                //@ts-ignore
                if (animation.animationName === "pulseAnimation") {
                    time = time || animation.currentTime;
                    animation.currentTime = time;
                }
            }
        }
    }, [props.pulse])

    return !props.hidden ? (
        <TooltipHost delay={TooltipDelay.long} content={props.tooltip} directionalHint={DirectionalHint.leftCenter} styles={{ root: { width: "fit-content", height: "fit-content" } }}>
            <Root data-automation-id={props["data-automation-id"]}>
                {!!props.isConfirming ?
                    <Spinner styles={{ root: { width: "100%", height: "100%" }, circle: { width: "100%", height: "100%" } }} /> :
                    <CheckboxVisual
                        pulse={props.pulse}
                        onClick={!props.isConfirming && !props.isConfirmed ? props.onClick : undefined}
                        confirmedBackgroundColor={props.confirmedBackgroundColor}
                        isConfirmed={props.isConfirmed}
                        readOnly={props.readOnly}>
                        {
                            dgIcons.icon(
                                dgIconNames.DgConfirmationCheck,
                                '',
                                10,
                                10,
                                homeGray,
                                ''
                            )
                        }
                    </CheckboxVisual>
                }
            </Root>
        </TooltipHost>
    ) : null
}
